export interface FinanceSalesReportCacheFilters {
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  warehouse: string;
  isOutward: boolean;
  product: string;
  brand: string;
  district: string;
  driver: string;
}

export interface FinanceSalesReportRequestFilters {
  fromDate: string;
  toDate: string;
  fromTime?: string;
  toTime?: string;
  brand?: string | null;
  product?: string | null;
  district?: string | null;
  driver?: string | null;
  warehouse?: string | null;
  isOutward?: boolean;
  pagination?: boolean;
  limit?: number;
  skip?: number;
}

export interface FinanceSalesReportParams {
  fromDate: string;
  toDate: string;
  "brands[0]"?: string;
  "variants[0]"?: string;
  "districts[0]"?: string;
  driver?: string;
  warehouse?: string;
  isOutward?: boolean;
  fromTime?: string;
  toTime?: string;
  utcOffset?: number;
  pagination?: boolean;
  limit?: number;
  skip?: number;
}

export const normalizeTimeToSeconds = (
  value: string | undefined,
  fallback: string,
): string => {
  const [hours = "00", minutes = "00", seconds = "00"] = (value || fallback).split(":");
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:${seconds.padStart(2, "0")}`;
};

export const buildFinanceSalesReportParams = (
  filters: FinanceSalesReportRequestFilters,
): FinanceSalesReportParams => ({
  fromDate: filters.fromDate,
  toDate: filters.toDate,
  ...(filters.brand ? { "brands[0]": filters.brand } : {}),
  ...(filters.product ? { "variants[0]": filters.product } : {}),
  ...(filters.district ? { "districts[0]": filters.district } : {}),
  ...(filters.driver ? { driver: filters.driver } : {}),
  ...(filters.warehouse ? { warehouse: filters.warehouse } : {}),
  ...(filters.isOutward ? {
    isOutward: true,
    fromTime: filters.fromTime || "00:00:00",
    toTime: filters.toTime || "23:59:59",
    utcOffset: -180,
  } : {}),
  ...(filters.pagination ? {
    pagination: true,
    limit: filters.limit || 0,
    skip: filters.skip || 0,
  } : {}),
});

export const financeSalesReportCacheKey = (
  filters: FinanceSalesReportCacheFilters,
): string => JSON.stringify(filters);

export class FinanceReportResponseCache {
  private entries = new Map<string, { expiresAt: number; json: string }>();
  private pending = new Map<string, Promise<unknown>>();
  private generation = 0;

  clear() {
    this.generation++;
    this.entries.clear();
    this.pending.clear();
  }

  async load<Result>(key: string, request: () => Promise<Result>, valid: (result: Result) => boolean): Promise<Result> {
    const cached = this.entries.get(key);
    if (cached && cached.expiresAt > Date.now()) return JSON.parse(cached.json) as Result;
    this.entries.delete(key);
    const existing = this.pending.get(key);
    if (existing) {
      const result = await existing as Result;
      return valid(result) ? JSON.parse(JSON.stringify(result)) as Result : result;
    }
    const generation = this.generation;
    const pending = request();
    this.pending.set(key, pending);
    try {
      const result = await pending;
      if (valid(result) && generation === this.generation) {
        const json = JSON.stringify(result);
        if (json.length <= 2_000_000) {
          for (const [entryKey, entry] of this.entries) {
            if (entry.expiresAt <= Date.now()) this.entries.delete(entryKey);
          }
          this.entries.set(key, { expiresAt: Date.now() + 30_000, json });
          while (this.entries.size > 8 || Array.from(this.entries.values()).reduce((size, entry) => size + entry.json.length, 0) > 4_000_000) {
            const oldest = this.entries.keys().next().value;
            if (oldest === undefined) break;
            this.entries.delete(oldest);
          }
        }
      }
      return result;
    } finally {
      if (this.pending.get(key) === pending) this.pending.delete(key);
    }
  }
}
